import { mande, type MandeError } from 'mande';
import type { Router } from 'vue-router';

import { auth0 } from '@/auth0';
import { fetchWithTimeout, withTimeout } from '@/utils/networkUtils';

type EventProps = Record<string, unknown>;

interface UsageEvent {
  event_type: string;
  occurred_at: string;
  page_path?: string;
  page_name?: string;
  properties: EventProps;
}

interface StoredEvent {
  id: string;
  owner?: string;
  event: UsageEvent;
}

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 20;
const MAX_EVENTS_PER_REQUEST = 50;
const MAX_QUEUE = 500;
const TOKEN_TIMEOUT_MS = 5000;
const POST_TIMEOUT_MS = 15000;
const STORAGE_KEY = 'etn_readings_usage_queue';
const LAST_ACTIVITY_KEY = 'etn_readings_last_activity';
const REACTIVATION_MINUTES = 30;
const ACTIVITY_CHECK_INTERVAL_MS = 30000;

export const arrivedFromLogin =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('code');

let timer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<void> | null = null;

function detectDeviceType(): string {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return 'unknown';
  const ua = navigator.userAgent;
  if (/iPad|Android(?!.*Mobile)|Tablet|Silk(?!.*Mobile)/i.test(ua)) return 'tablet';
  if (/Mobi|iPhone|iPod|Android.*Mobile|Opera Mini|IEMobile/i.test(ua)) return 'mobile';
  if (
    /Macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  )
    return 'tablet';
  return 'desktop';
}

function parseBrowser(ua: string) {
  const version = (pattern: RegExp) => {
    const found = ua.match(pattern);
    return found ? Number.parseInt(found[1], 10) : undefined;
  };
  if (/Edg\//i.test(ua)) return { browser: 'edge', browser_version: version(/Edg\/(\d+)/i) };
  if (/OPR\/|Opera\//i.test(ua))
    return { browser: 'opera', browser_version: version(/(?:OPR|Opera)\/(\d+)/i) };
  if (/SamsungBrowser/i.test(ua))
    return { browser: 'samsung', browser_version: version(/SamsungBrowser\/(\d+)/i) };
  if (/Firefox\/|FxiOS/i.test(ua))
    return { browser: 'firefox', browser_version: version(/(?:Firefox|FxiOS)\/(\d+)/i) };
  if (/Chrome\/|CriOS/i.test(ua))
    return { browser: 'chrome', browser_version: version(/(?:Chrome|CriOS)\/(\d+)/i) };
  if (/Safari\//i.test(ua))
    return { browser: 'safari', browser_version: version(/Version\/(\d+)/i) };
  return { browser: 'other', browser_version: undefined };
}

function parseOs(ua: string): string {
  if (/Windows/i.test(ua)) return 'windows';
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/CrOS/i.test(ua)) return 'chromeos';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macos';
  if (/Linux/i.test(ua)) return 'linux';
  return 'other';
}

function pageViewMetadata(): EventProps {
  const ua = navigator.userAgent || '';
  return {
    device_type: detectDeviceType(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio || 1,
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    language: navigator.language || undefined,
    ...parseBrowser(ua),
    os: parseOs(ua),
    timezone_offset_minutes: -new Date().getTimezoneOffset()
  };
}

function readQueue(): StoredEvent[] {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeQueue(events: StoredEvent[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_QUEUE)));
  } catch {
    /* storage full or unavailable */
  }
}

// Re-queue only failures that can succeed later; a rejected batch would otherwise be re-sent forever.
function isRetryable(error: unknown): boolean {
  const status = (error as MandeError)?.response?.status;
  return !status || status === 401 || status === 408 || status === 429 || status >= 500;
}

function removeFromQueue(ids: Set<string>) {
  writeQueue(readQueue().filter((stored) => !ids.has(stored.id)));
}

function currentOwner(): string | undefined {
  const user = auth0.user.value;
  return user?.sub ? `${user.sub}|${user['https://app.etainabl.com/companyId'] ?? ''}` : undefined;
}

const sendableBy = (owner: string | undefined) => (stored: StoredEvent) =>
  !stored.owner || stored.owner === owner;

const newEventId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// Report a chunk as done when the API accepted it or rejected it for good.
async function sendChunk(events: UsageEvent[]): Promise<boolean> {
  try {
    const token = await withTimeout(auth0.getAccessTokenSilently(), TOKEN_TIMEOUT_MS);
    const api = mande(`${import.meta.env.VITE_APP_API_URL}/usage`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    await fetchWithTimeout(
      (signal) => api.post('events', { events }, { keepalive: true, signal }),
      POST_TIMEOUT_MS
    );
    return true;
  } catch (error) {
    return !isRetryable(error);
  }
}

async function sendInOrder(chunks: StoredEvent[][]): Promise<void> {
  const [chunk, ...rest] = chunks;
  if (!chunk) return;
  const done = await sendChunk(chunk.map((stored) => stored.event));
  if (!done) return;
  removeFromQueue(new Set(chunk.map((stored) => stored.id)));
  await sendInOrder(rest);
}

async function deliver() {
  const sendable = readQueue().filter(sendableBy(currentOwner()));
  const chunks = Array.from(
    { length: Math.ceil(sendable.length / MAX_EVENTS_PER_REQUEST) },
    (_, index) =>
      sendable.slice(index * MAX_EVENTS_PER_REQUEST, (index + 1) * MAX_EVENTS_PER_REQUEST)
  );
  await sendInOrder(chunks);
}

// Send stored events from the current login or from before any login, after any send already running.
export function flush(): Promise<void> {
  if (!auth0.isAuthenticated.value) return Promise.resolve();
  if (inFlight) return inFlight.then(() => flush());
  inFlight = deliver().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

export function track(eventType: string, properties: EventProps = {}) {
  const enriched =
    eventType === 'page_view' ? { ...pageViewMetadata(), ...properties } : properties;
  const event: UsageEvent = {
    event_type: eventType,
    occurred_at: new Date().toISOString(),
    properties: enriched
  };
  if (typeof enriched.page_path === 'string') event.page_path = enriched.page_path;
  if (typeof enriched.page_name === 'string') event.page_name = enriched.page_name;
  const owner = currentOwner();
  const queue = [...readQueue(), { id: newEventId(), owner, event }];
  writeQueue(queue);
  if (queue.filter(sendableBy(owner)).length >= FLUSH_BATCH_SIZE) flush();
  else scheduleFlush();
}

function lastActivity(): number {
  try {
    return Number(window.localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
  } catch {
    return 0;
  }
}

function bumpActivity() {
  try {
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch {
    /* storage full or unavailable */
  }
}

export function maybeFireSessionStart({
  trigger = 'reactivation'
}: { trigger?: 'login' | 'reactivation' } = {}) {
  const last = lastActivity();
  const inactivityMinutes = last ? Math.floor((Date.now() - last) / 60000) : 9999;
  if (trigger === 'login' || inactivityMinutes >= REACTIVATION_MINUTES) {
    track('session_start', {
      trigger,
      inactivity_minutes: inactivityMinutes,
      device_type: detectDeviceType(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight
    });
  }
  bumpActivity();
}

export function installUsageTracking(router: Router) {
  router.afterEach((to, _from, failure) => {
    if (failure) return;
    track('page_view', { page_path: to.path, page_name: String(to.name || '') });
    if (auth0.isAuthenticated.value) maybeFireSessionStart();
  });

  let lastCheck = 0;
  const onActivity = () => {
    if (!auth0.isAuthenticated.value || Date.now() - lastCheck < ACTIVITY_CHECK_INTERVAL_MS) return;
    lastCheck = Date.now();
    maybeFireSessionStart();
  };
  ['pointerdown', 'keydown'].forEach((eventName) =>
    window.addEventListener(eventName, onActivity, { passive: true })
  );

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
